import { clientStorage } from "@/lib/clientStorage";
import React, { useState, useEffect } from "react";
import { 
  Search, 
  Database, 
  RefreshCw, 
  Download, 
  Server, 
  CheckCircle, 
  AlertTriangle, 
  Shield,
  Clock,
  User,
  Info,
  HardDrive,
  Key,
  Cpu,
  Lock,
  AlertOctagon,
  FileText,
  Activity,
  CheckCircle2,
  Trash2,
  Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { base44 } from "@/api/base44Client";

export default function AdminAuditLogsView() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("logs"); // "logs" | "backups" | "licensing" | "diagnostics"
  
  // State for audit logs
  const [logs, setLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Phase 5 Audit Filters
  const [filterUser, setFilterUser] = useState("");
  const [filterMatter, setFilterMatter] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [filterDateStart, setFilterDateStart] = useState("");
  const [filterDateEnd, setFilterDateEnd] = useState("");
  
  // State for backups
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  const [backupStatus, setBackupStatus] = useState(null);
  
  // Backup verification & restore wizard state
  const [verifyingFile, setVerifyingFile] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreConfirmText, setRestoreConfirmText] = useState("");
  const [restoreAdminPassword, setRestoreAdminPassword] = useState("");
  const [restoringState, setRestoringState] = useState(false); // false | "loading" | "success"

  // Licensing state
  const [licenseData, setLicenseData] = useState(null);
  const [loadingLicense, setLoadingLicense] = useState(false);
  const [licenseKeyInput, setLicenseKeyInput] = useState("");
  const [activatingLicense, setActivatingLicense] = useState(false);

  // Updates state
  const [updatesData, setUpdatesData] = useState(null);
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  const [installingUpdate, setInstallingUpdate] = useState(false);

  // Diagnostics health state
  const [healthData, setHealthData] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(false);

  const getHeaders = () => {
    const token = clientStorage.getItem("clsp_session_token");
    return {
      "Content-Type": "application/json",
      ...(token ? { "Authorization": `Bearer ${token}` } : {})
    };
  };

  // 1. Audit Logs retrieval
  const fetchLogs = async () => {
    setLoadingLogs(true);
    try {
      const data = await base44.admin.auditLogs({
        user: filterUser,
        matter: filterMatter,
        action: filterAction,
        date_start: filterDateStart,
        date_end: filterDateEnd
      });
      setLogs(data);
    } catch (err) {
      console.error(err);
      toast.error("Error retrieving audit logs: " + err.message);
    } finally {
      setLoadingLogs(false);
    }
  };

  // 2. Backups retrieval
  const fetchBackups = async () => {
    setLoadingBackups(true);
    try {
      // Fetch backup list
      const resList = await fetch("/api/admin/backup/list", {
        headers: getHeaders()
      });
      if (!resList.ok) throw new Error("Failed to fetch backups list");
      const listData = await resList.json();
      setBackups(listData);

      // Fetch backup status dashboard metrics
      const statusData = await base44.admin.backup.status();
      setBackupStatus(statusData);
    } catch (err) {
      console.error(err);
      toast.error("Error retrieving backup information: " + err.message);
    } finally {
      setLoadingBackups(false);
    }
  };

  const triggerBackup = async () => {
    setBackingUp(true);
    toast.info("Generating full GFS system backup (db, uploads, configurations)...");
    try {
      const data = await base44.admin.backup.manual();
      toast.success(`GFS backup package generated successfully: ${data.filename}`);
      fetchBackups();
      fetchLogs(); // Reload logs
    } catch (err) {
      console.error(err);
      toast.error("Backup creation failed: " + err.message);
    } finally {
      setBackingUp(false);
    }
  };

  const verifyBackup = async (file) => {
    setVerifyingFile(file.filename);
    setVerificationResult(null);
    try {
      const result = await base44.admin.backup.verify(file.type, file.filename);
      setVerificationResult({
        filename: file.filename,
        valid: result.valid,
        sizeBytes: result.sizeBytes,
        dbSize: result.dbSize,
        filesCount: result.filesCount,
        error: result.error
      });
      if (result.valid) {
        toast.success(`Backup file ${file.filename} verified successfully! integrity check passed.`);
      } else {
        toast.error(`Backup integrity check failed: ${result.error}`);
      }
    } catch (err) {
      console.error(err);
      toast.error("Integrity verification failed: " + err.message);
    } finally {
      setVerifyingFile(null);
    }
  };

  const handleRestoreSubmit = async (e) => {
    e.preventDefault();
    if (!restoreTarget) return;
    if (restoreConfirmText !== "I UNDERSTAND") {
      toast.error("Please type the confirmation phrase exactly.");
      return;
    }
    if (!restoreAdminPassword) {
      toast.error("Administrator password is required for confirmation.");
      return;
    }

    setRestoringState("loading");
    try {
      // Re-authenticate user locally
      const authResponse = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user.email, password: restoreAdminPassword })
      });
      
      if (!authResponse.ok) {
        throw new Error("Invalid administrator password authentication failed.");
      }

      // Execute restore
      const restoreRes = await base44.admin.backup.restore(restoreTarget.type, restoreTarget.filename);
      setRestoringState("success");
      toast.success("System restore completed successfully! Overwriting SQLite database.");
      
      setTimeout(() => {
        // Logout user and trigger window reload to reinitialize
        clientStorage.clear();
        window.location.href = "/";
      }, 3000);
    } catch (err) {
      console.error(err);
      toast.error("Restore failed: " + err.message);
      setRestoringState(false);
    }
  };

  const downloadBackup = async (file) => {
    try {
      const url = `/api/admin/backup/download/${encodeURIComponent(file.type)}/${encodeURIComponent(file.filename)}`;
      const res = await fetch(url, { headers: getHeaders() });
      if (!res.ok) throw new Error("Download failed");
      
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", file.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(`Downloaded: ${file.filename}`);
    } catch (err) {
      console.error(err);
      toast.error("Error downloading backup: " + err.message);
    }
  };

  // 3. Licensing functions
  const fetchLicenseDetails = async () => {
    setLoadingLicense(true);
    try {
      const data = await base44.admin.license.status();
      setLicenseData(data);
      setLicenseKeyInput(data.licenseKey || "");
    } catch (err) {
      console.error(err);
      toast.error("Failed to load license details: " + err.message);
    } finally {
      setLoadingLicense(false);
    }
  };

  const handleActivateLicense = async (e) => {
    e.preventDefault();
    if (!licenseKeyInput) return;
    setActivatingLicense(true);
    try {
      const data = await base44.admin.license.activate(licenseKeyInput);
      toast.success("License key activated successfully!");
      fetchLicenseDetails();
      fetchLogs(); // Reload logs
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to activate license.");
    } finally {
      setActivatingLicense(false);
    }
  };

  const handleDeactivateLicense = async () => {
    if (!window.confirm("Are you sure you want to deactivate and remove this license? The system will revert to an unactivated demo state.")) return;
    try {
      await base44.admin.license.deactivate();
      toast.success("License deactivated successfully.");
      fetchLicenseDetails();
      fetchLogs();
    } catch (err) {
      console.error(err);
      toast.error("Failed to deactivate license: " + err.message);
    }
  };

  // 4. Update functions
  const handleCheckUpdates = async () => {
    setCheckingUpdates(true);
    try {
      const data = await base44.admin.updates.check();
      setUpdatesData(data);
      if (data.hasUpdate) {
        toast.info(`New version ${data.latestVersion} is available!`);
      } else {
        toast.success("Your CLS Portal is up to date (Version 1.0.0).");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch update info: " + err.message);
    } finally {
      setCheckingUpdates(false);
    }
  };

  const handleInstallUpdate = async () => {
    if (!window.confirm("Installing updates requires restarting background node processes. Unsaved form drafts will not be affected. Proceed?")) return;
    setInstallingUpdate(true);
    try {
      await base44.admin.updates.install();
      toast.success("Software updates installed successfully!");
      handleCheckUpdates();
      fetchLogs();
    } catch (err) {
      console.error(err);
      toast.error("Failed to install update: " + err.message);
    } finally {
      setInstallingUpdate(false);
    }
  };

  // 5. Diagnostics Health retrieval
  const fetchHealthDiagnostics = async () => {
    setLoadingHealth(true);
    try {
      const data = await base44.admin.healthCheck();
      setHealthData(data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to retrieve system health metrics: " + err.message);
    } finally {
      setLoadingHealth(false);
    }
  };

  useEffect(() => {
    if (activeTab === "logs") {
      fetchLogs();
    } else if (activeTab === "backups") {
      fetchBackups();
    } else if (activeTab === "licensing") {
      fetchLicenseDetails();
      handleCheckUpdates();
    } else if (activeTab === "diagnostics") {
      fetchHealthDiagnostics();
    }
  }, [activeTab, filterUser, filterMatter, filterAction, filterDateStart, filterDateEnd]);

  // Filter logs by search query
  const filteredLogs = logs.filter(log => {
    const query = searchQuery.toLowerCase();
    return (
      (log.user_email && log.user_email.toLowerCase().includes(query)) ||
      (log.user_id && log.user_id.toLowerCase().includes(query)) ||
      (log.action && log.action.toLowerCase().includes(query)) ||
      (log.details && log.details.toLowerCase().includes(query)) ||
      (log.matter_id && log.matter_id.toLowerCase().includes(query)) ||
      (log.ip_address && log.ip_address.toLowerCase().includes(query))
    );
  });

  const getActionBadgeColor = (action) => {
    const act = (action || "").toUpperCase();
    if (act.includes("LOGIN") || act.includes("LOGOUT") || act.includes("AUTH")) {
      return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-800";
    }
    if (act.includes("CREATE") || act.includes("ADD") || act.includes("INSERT")) {
      return "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-400 dark:border-green-800";
    }
    if (act.includes("UPDATE") || act.includes("EDIT") || act.includes("MODIFY")) {
      return "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800";
    }
    if (act.includes("DELETE") || act.includes("REMOVE") || act.includes("PURGE")) {
      return "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-800";
    }
    if (act.includes("BACKUP") || act.includes("RESTORE")) {
      return "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-400 dark:border-purple-800";
    }
    return "bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-900/40 dark:text-slate-400 dark:border-slate-800";
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (!bytes) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Title block */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-600" />
            System Administration Console
          </h1>
          <p className="text-slate-500 dark:text-slate-450 text-sm mt-1">
            Local deployment operations, security audits, database replication, and system diagnostics
          </p>
        </div>
        
        {/* Connection / Status Badge */}
        <div className="flex items-center gap-4">
          <div className="bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-2 flex items-center gap-2.5 text-xs font-semibold text-slate-600 dark:text-slate-350">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
            <span>Local Service: Active</span>
          </div>
          <div className="bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-2 flex items-center gap-2.5 text-xs font-semibold text-slate-600 dark:text-slate-350">
            <Database className="w-4 h-4 text-blue-500" />
            <span>SQLite Active</span>
          </div>
        </div>
      </div>

      {/* Tabs bar */}
      <div className="flex border-b border-slate-200 dark:border-slate-800 mb-6 gap-2 overflow-x-auto">
        <button
          onClick={() => setActiveTab("logs")}
          className={`pb-3 px-4 font-bold text-sm transition-all border-b-2 -mb-[2px] flex items-center gap-2 whitespace-nowrap cursor-pointer ${
            activeTab === "logs"
              ? "border-blue-600 text-blue-600 dark:text-blue-400"
              : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
          }`}
        >
          <Clock className="w-4 h-4" />
          Security Audit Logs
        </button>
        <button
          onClick={() => setActiveTab("backups")}
          className={`pb-3 px-4 font-bold text-sm transition-all border-b-2 -mb-[2px] flex items-center gap-2 whitespace-nowrap cursor-pointer ${
            activeTab === "backups"
              ? "border-blue-600 text-blue-600 dark:text-blue-400"
              : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
          }`}
        >
          <HardDrive className="w-4 h-4" />
          Database Backups
        </button>
        <button
          onClick={() => setActiveTab("licensing")}
          className={`pb-3 px-4 font-bold text-sm transition-all border-b-2 -mb-[2px] flex items-center gap-2 whitespace-nowrap cursor-pointer ${
            activeTab === "licensing"
              ? "border-blue-600 text-blue-600 dark:text-blue-400"
              : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
          }`}
        >
          <Key className="w-4 h-4" />
          Licensing & Updates
        </button>
        <button
          onClick={() => setActiveTab("diagnostics")}
          className={`pb-3 px-4 font-bold text-sm transition-all border-b-2 -mb-[2px] flex items-center gap-2 whitespace-nowrap cursor-pointer ${
            activeTab === "diagnostics"
              ? "border-blue-600 text-blue-600 dark:text-blue-400"
              : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
          }`}
        >
          <Cpu className="w-4 h-4" />
          System Diagnostics
        </button>
      </div>

      {/* Logs View */}
      {activeTab === "logs" && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 p-4 rounded-2xl shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <Shield className="w-4 h-4 text-blue-500" />
                Filter Logs
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilterUser("");
                  setFilterMatter("");
                  setFilterAction("");
                  setFilterDateStart("");
                  setFilterDateEnd("");
                  setSearchQuery("");
                }}
                className="text-[10px] font-bold uppercase text-slate-400 hover:text-slate-600 h-6 px-2 cursor-pointer"
              >
                Clear Filters
              </Button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">User / Staff Email</label>
                <Input
                  type="text"
                  placeholder="Filter by user..."
                  value={filterUser}
                  onChange={(e) => setFilterUser(e.target.value)}
                  className="bg-slate-50/50 dark:bg-slate-950/50 border-slate-200 dark:border-slate-800 rounded-xl text-xs h-9"
                />
              </div>
              
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Matter Reference</label>
                <Input
                  type="text"
                  placeholder="Filter by matter..."
                  value={filterMatter}
                  onChange={(e) => setFilterMatter(e.target.value)}
                  className="bg-slate-50/50 dark:bg-slate-950/50 border-slate-200 dark:border-slate-800 rounded-xl text-xs h-9"
                />
              </div>
              
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Action Type</label>
                <select
                  value={filterAction}
                  onChange={(e) => setFilterAction(e.target.value)}
                  className="w-full bg-slate-50/50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 rounded-xl text-xs h-9 px-3 focus:ring-0 focus:outline-none text-slate-700 dark:text-slate-300"
                >
                  <option value="">All Actions</option>
                  <option value="login_success">Login Success</option>
                  <option value="login_failed">Login Failed</option>
                  <option value="logout">Logout</option>
                  <option value="create_record">Create Record</option>
                  <option value="edit_record">Edit Record</option>
                  <option value="delete_record">Delete Record</option>
                  <option value="restore_version">Restore Version</option>
                  <option value="upload_document">Upload Document</option>
                  <option value="export_record">Export Record</option>
                  <option value="print_report">Print Report</option>
                  <option value="user_registration">User Admin</option>
                  <option value="database_backup">Database Backup</option>
                  <option value="database_restore">Database Restore</option>
                </select>
              </div>
              
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Start Date</label>
                <Input
                  type="date"
                  value={filterDateStart}
                  onChange={(e) => setFilterDateStart(e.target.value)}
                  className="bg-slate-50/50 dark:bg-slate-950/50 border-slate-200 dark:border-slate-800 rounded-xl text-xs h-9"
                />
              </div>
              
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">End Date</label>
                <Input
                  type="date"
                  value={filterDateEnd}
                  onChange={(e) => setFilterDateEnd(e.target.value)}
                  className="bg-slate-50/50 dark:bg-slate-950/50 border-slate-200 dark:border-slate-800 rounded-xl text-xs h-9"
                />
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 border-t border-slate-100 dark:border-slate-800">
              <div className="relative w-full sm:max-w-md">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-slate-400" />
                </span>
                <Input
                  type="text"
                  placeholder="Local search within results..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-slate-50/50 dark:bg-slate-950/50 border-slate-250 dark:border-slate-850 rounded-xl text-xs h-9 w-full"
                />
              </div>
              
              <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchLogs}
                  disabled={loadingLogs}
                  className="rounded-xl border-slate-200 font-bold uppercase tracking-wider text-[10px] text-slate-650 flex items-center gap-1.5 h-9"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${loadingLogs ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
                <div className="text-[10px] font-bold text-slate-450 uppercase tracking-wider px-2">
                  Showing {filteredLogs.length} of {logs.length} logs
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto max-h-[600px]">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/60 dark:bg-slate-950/60 border-b border-slate-100 dark:border-slate-800 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    <th className="px-6 py-4">Timestamp</th>
                    <th className="px-6 py-4">Staff Member</th>
                    <th className="px-6 py-4">Action</th>
                    <th className="px-6 py-4">Details</th>
                    <th className="px-6 py-4 text-right">IP Address</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                  {loadingLogs && logs.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center text-slate-400 dark:text-slate-500 italic">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
                        </div>
                        Loading system audit logs...
                      </td>
                    </tr>
                  ) : filteredLogs.length > 0 ? (
                    filteredLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-slate-50/40 dark:hover:bg-slate-950/40 transition-colors">
                        <td className="px-6 py-4 text-slate-400 dark:text-slate-500 font-semibold whitespace-nowrap text-xs">
                          {new Date(log.timestamp).toLocaleString("en-AU")}
                        </td>
                        <td className="px-6 py-4 font-semibold text-slate-700 dark:text-slate-350 font-sans">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-xs text-slate-650 font-bold border border-slate-200 dark:border-slate-700">
                              {log.user_email ? log.user_email[0].toUpperCase() : <User className="w-3.5 h-3.5" />}
                            </div>
                            <div className="flex flex-col">
                              <span className="text-xs leading-none">{log.user_email || "System/Cron"}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2.5 py-1 text-[10px] font-bold border rounded-full ${getActionBadgeColor(log.action)}`}>
                            {log.action}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-slate-600 dark:text-slate-400 font-medium text-xs max-w-sm truncate" title={log.details}>
                          <div className="flex items-center flex-wrap gap-1.5 font-sans">
                            <span>{log.details || "—"}</span>
                            {log.matter_id && (
                              <span className="bg-slate-100 dark:bg-slate-800 text-slate-650 dark:text-slate-400 px-1.5 py-0.5 rounded text-[9px] font-bold border border-slate-200/50 dark:border-slate-700/50">
                                Matter: {log.matter_id}
                              </span>
                            )}
                            {log.entity_type && (
                              <span className="bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-450 px-1.5 py-0.5 rounded text-[9px] font-bold border border-slate-200/50 dark:border-slate-700/50">
                                Type: {log.entity_type}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right text-slate-450 dark:text-slate-500 font-mono text-xs whitespace-nowrap">
                          {log.ip_address || "127.0.0.1"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center text-slate-400 dark:text-slate-500 italic">
                        No logs matched the search criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Backups View */}
      {activeTab === "backups" && (
        <div className="space-y-6">
          {/* GFS Dashboard Row */}
          {backupStatus && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 p-5 rounded-2xl shadow-sm">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-450 block mb-1">Total Backup Storage</span>
                <span className="text-xl font-bold text-slate-800 dark:text-slate-100 block">{formatBytes(backupStatus.totalSize)}</span>
                <span className="text-xs text-slate-400 dark:text-slate-500 mt-1 block">across all retention folders</span>
              </div>
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 p-5 rounded-2xl shadow-sm">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-450 block mb-1">Last Run Time</span>
                <span className="text-sm font-bold text-slate-800 dark:text-slate-200 block truncate" title={backupStatus.lastBackupTime ? new Date(backupStatus.lastBackupTime).toLocaleString("en-AU") : "Never"}>
                  {backupStatus.lastBackupTime ? new Date(backupStatus.lastBackupTime).toLocaleString("en-AU") : "Never"}
                </span>
                <span className="text-xs text-slate-400 dark:text-slate-500 mt-1.5 block">automatic GFS scheduler active</span>
              </div>
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 p-5 rounded-2xl shadow-sm">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-450 block mb-1">Last Run Status</span>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className={`w-2.5 h-2.5 rounded-full ${backupStatus.lastBackupStatus === "success" ? "bg-emerald-500" : "bg-rose-500"}`}></span>
                  <span className="text-sm font-bold capitalize text-slate-700 dark:text-slate-200">
                    {backupStatus.lastBackupStatus === "success" ? "Success" : "Failed / None"}
                  </span>
                </div>
                <span className="text-xs text-slate-400 dark:text-slate-500 mt-1 block">verified automatically</span>
              </div>
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 p-5 rounded-2xl shadow-sm">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-450 block mb-1">GFS File Counts</span>
                <span className="text-xs text-slate-650 dark:text-slate-400 font-semibold block leading-normal mt-1">
                  Daily: {backupStatus.counts.daily} / 30 <br/>
                  Weekly: {backupStatus.counts.weekly} / 12 | Monthly: {backupStatus.counts.monthly} / 12
                </span>
              </div>
            </div>
          )}

          {/* Backup verification result banner if exists */}
          {verificationResult && (
            <div className={`border rounded-2xl p-4 flex items-start gap-3.5 shadow-sm ${
              verificationResult.valid 
                ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-800 dark:text-emerald-350" 
                : "bg-rose-500/10 border-rose-500/20 text-rose-800 dark:text-rose-350"
            }`}>
              {verificationResult.valid ? (
                <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-emerald-500" />
              ) : (
                <AlertOctagon className="w-5 h-5 flex-shrink-0 mt-0.5 text-rose-500" />
              )}
              <div className="flex-1 text-xs">
                <h4 className="font-bold text-sm mb-1">Backup Verification Integrity Result</h4>
                <p className="font-mono bg-slate-900/15 dark:bg-slate-900/40 p-1.5 rounded inline-block mb-2 text-[10px]">
                  {verificationResult.filename}
                </p>
                {verificationResult.valid ? (
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Package Integrity Check: **PASS**</li>
                    <li>Database Signature (`SQLite format 3`): **VALID**</li>
                    <li>Compressed Package Size: {formatBytes(verificationResult.sizeBytes)}</li>
                    <li>Inner SQLite File Size: {formatBytes(verificationResult.dbSize)}</li>
                    <li>Inner Attachments count: {verificationResult.filesCount} uploads</li>
                  </ul>
                ) : (
                  <p className="font-semibold text-rose-600 dark:text-rose-400">
                    Failed verification: {verificationResult.error}
                  </p>
                )}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setVerificationResult(null)}
                  className="mt-2 text-[10px] uppercase font-bold text-slate-450 h-7 border border-slate-200 dark:border-slate-800 rounded-lg hover:bg-slate-100"
                >
                  Dismiss Report
                </Button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            {/* Left Column: Management & Rules */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
                <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2 mb-4">
                  <Server className="w-5 h-5 text-blue-600" />
                  Backup Actions
                </h2>
                
                <p className="text-xs text-slate-500 dark:text-slate-450 leading-relaxed mb-6 font-sans">
                  Database GFS backups are stored locally. Each package contains the SQLite database, uploaded matter files, and application config.
                  <code className="block mt-2 p-2 bg-slate-50 dark:bg-slate-950 rounded text-slate-650 dark:text-slate-400 font-mono text-[10px] break-all border border-slate-100 dark:border-slate-850">
                    C:\CLSPortal\backups\
                  </code>
                </p>

                <Button
                  onClick={triggerBackup}
                  disabled={backingUp}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-blue-500/10 cursor-pointer"
                >
                  {backingUp ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Database className="w-4 h-4" />
                  )}
                  Create Manual Backup
                </Button>
              </div>

              <div className="bg-slate-50/50 dark:bg-slate-950/20 border border-slate-150 dark:border-slate-850 rounded-2xl p-6">
                <h3 className="text-xs font-bold text-slate-550 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5 mb-3">
                  <Info className="w-4 h-4 text-blue-550" />
                  Automatic Backup Policies
                </h3>
                <ul className="space-y-3 text-xs text-slate-500 dark:text-slate-450 leading-relaxed font-sans">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Daily Backups:</strong> Saved nightly. Retains the newest 30 daily files.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Weekly Backups:</strong> Saved Sunday nights. Retains newest 12 weekly files.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Monthly Backups:</strong> Saved 1st of month. Retains newest 12 monthly files.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Recovery Warnings:</strong> Restoring database closes all database locks, overwrites the storage engine, and logs off active users.</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right Column: Backup Files list */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-bold text-slate-850 dark:text-slate-200 flex items-center gap-2">
                  <HardDrive className="w-5 h-5 text-blue-500" />
                  Backup Storage History
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchBackups}
                  disabled={loadingBackups}
                  className="rounded-xl border-slate-200 text-slate-650 flex items-center gap-1.5 cursor-pointer"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${loadingBackups ? "animate-spin" : ""}`} />
                  Refresh List
                </Button>
              </div>

              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto max-h-[500px]">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50/60 dark:bg-slate-950/60 border-b border-slate-100 dark:border-slate-800 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        <th className="px-6 py-4">Backup Filename</th>
                        <th className="px-6 py-4">Type</th>
                        <th className="px-6 py-4">Size</th>
                        <th className="px-6 py-4">Created Time</th>
                        <th className="px-6 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                      {loadingBackups && backups.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-6 py-12 text-center text-slate-400 dark:text-slate-500 italic">
                            <RefreshCw className="w-5 h-5 animate-spin text-blue-500 mx-auto mb-2" />
                            Loading backups...
                          </td>
                        </tr>
                      ) : backups.length > 0 ? (
                        backups.map((file) => (
                          <tr key={file.filename} className="hover:bg-slate-50/40 dark:hover:bg-slate-950/40 transition-colors">
                            <td className="px-6 py-4 font-mono font-bold text-slate-700 dark:text-slate-350 text-xs">
                              {file.filename}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-xs">
                              <span className="bg-slate-100 dark:bg-slate-800 text-slate-650 dark:text-slate-350 px-2 py-0.5 rounded-full font-bold uppercase text-[9px] border">
                                {file.type}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-slate-550 dark:text-slate-450 text-xs">
                              {formatBytes(file.sizeBytes)}
                            </td>
                            <td className="px-6 py-4 text-slate-400 dark:text-slate-500 text-xs whitespace-nowrap">
                              {new Date(file.createdAt).toLocaleString("en-AU")}
                            </td>
                            <td className="px-6 py-4 text-right flex items-center justify-end gap-1.5 whitespace-nowrap">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => downloadBackup(file)}
                                className="rounded-xl border-slate-200 text-slate-750 hover:bg-slate-100 h-8 font-bold text-xs inline-flex items-center gap-1 cursor-pointer"
                                title="Download backup file"
                              >
                                <Download className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={verifyingFile === file.filename}
                                onClick={() => verifyBackup(file)}
                                className="rounded-xl border-slate-200 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/20 h-8 font-bold text-xs inline-flex items-center gap-1 cursor-pointer"
                                title="Verify package integrity"
                              >
                                {verifyingFile === file.filename ? (
                                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-3.5 h-3.5" />
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setRestoreTarget(file);
                                  setRestoreConfirmText("");
                                  setRestoreAdminPassword("");
                                  setRestoringState(false);
                                }}
                                className="rounded-xl border-slate-200 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/20 h-8 font-bold text-xs inline-flex items-center gap-1 cursor-pointer"
                                title="Restore database from this backup"
                              >
                                <RefreshCw className="w-3.5 h-3.5" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="px-6 py-12 text-center text-slate-400 dark:text-slate-500 italic">
                            No backup files found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Restore Confirmation Modal Wizard */}
          {restoreTarget && (
            <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-slate-900 border border-slate-800 max-w-md w-full rounded-2xl shadow-2xl p-6 overflow-hidden">
                <div className="text-center mb-6">
                  <div className="w-12 h-12 bg-rose-500/10 border border-rose-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <AlertOctagon className="w-6 h-6 text-rose-500" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-100">Critical: Restore System Database</h3>
                  <p className="text-xs text-slate-400 mt-1 font-sans">
                    Restoring from backup is a destructive action that overwrites the active environment.
                  </p>
                </div>

                {restoringState === "loading" && (
                  <div className="text-center py-8 space-y-4">
                    <RefreshCw className="w-10 h-10 text-blue-500 animate-spin mx-auto" />
                    <p className="text-xs text-slate-400">Verifying backup signature and restoring tables...</p>
                  </div>
                )}

                {restoringState === "success" && (
                  <div className="text-center py-8 space-y-4">
                    <CheckCircle2 className="w-12 h-12 text-emerald-500 animate-bounce mx-auto" />
                    <h4 className="text-sm font-bold text-slate-100">Restore Complete!</h4>
                    <p className="text-xs text-slate-400 leading-normal">
                      Local SQLite file overwritten. Logging off session and reloading portal...
                    </p>
                  </div>
                )}

                {!restoringState && (
                  <form onSubmit={handleRestoreSubmit} className="space-y-4">
                    <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-3.5 text-xs text-rose-350 leading-relaxed font-sans">
                      <p className="font-bold mb-1 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> WARNING:</p>
                      This will replace all current clients, audits, checklists, tasks, and configurations with the contents of the backup package:
                      <code className="block mt-2 p-1.5 bg-slate-950 rounded text-slate-400 font-mono text-[9px] break-all border border-slate-800">
                        {restoreTarget.filename}
                      </code>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                        Type confirmation string "I UNDERSTAND"
                      </label>
                      <Input
                        type="text"
                        required
                        placeholder="Type 'I UNDERSTAND' here"
                        value={restoreConfirmText}
                        onChange={(e) => setRestoreConfirmText(e.target.value)}
                        className="bg-slate-950 border-slate-800 text-xs rounded-xl focus:border-rose-500 focus:ring-rose-500 text-slate-250 uppercase"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                        Enter Administrator Password
                      </label>
                      <div className="relative">
                        <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="w-4 h-4 text-slate-500" />
                        </span>
                        <Input
                          type="password"
                          required
                          placeholder="••••••••"
                          value={restoreAdminPassword}
                          onChange={(e) => setRestoreAdminPassword(e.target.value)}
                          className="pl-9 bg-slate-950 border-slate-800 text-xs rounded-xl focus:border-rose-500 focus:ring-rose-500 text-slate-250"
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setRestoreTarget(null)}
                        className="flex-1 rounded-xl text-slate-400 hover:text-slate-200 border border-slate-800 text-xs cursor-pointer h-10 font-bold uppercase tracking-wider"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={restoreConfirmText !== "I UNDERSTAND" || !restoreAdminPassword}
                        className="flex-1 bg-rose-600 hover:bg-rose-700 disabled:bg-rose-600/40 text-white font-bold text-xs rounded-xl h-10 cursor-pointer uppercase tracking-wider"
                      >
                        Confirm Restore
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Licensing & Updates View */}
      {activeTab === "licensing" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* Licensing Info & Form */}
          <div className="lg:col-span-2 space-y-6">
            {loadingLicense ? (
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-6 shadow-sm flex items-center justify-center py-12 text-slate-400">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading license information...
              </div>
            ) : licenseData ? (
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-6">
                <div>
                  <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                    <Key className="w-5 h-5 text-blue-600" />
                    Product License Details
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-450 mt-1">
                    Manage law firm software authentication, offline keys, and system subscriptions.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-slate-50 dark:bg-slate-950 p-4 rounded-xl border">
                    <span className="text-[10px] font-bold uppercase text-slate-450 block">Firm Registration</span>
                    <span className="text-sm font-bold text-slate-700 dark:text-slate-200 mt-1 block">
                      {licenseData.firmName || "Jarrod Behan Law"}
                    </span>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-950 p-4 rounded-xl border">
                    <span className="text-[10px] font-bold uppercase text-slate-450 block">Installation ID</span>
                    <span className="text-xs font-mono text-slate-650 dark:text-slate-350 mt-1 block select-all">
                      {licenseData.installationId}
                    </span>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-950 p-4 rounded-xl border">
                    <span className="text-[10px] font-bold uppercase text-slate-450 block">License Key Status</span>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className={`w-2 h-2 rounded-full ${licenseData.licenseStatus === "active" ? "bg-emerald-500" : "bg-amber-500"}`}></span>
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-705 dark:text-slate-305">
                        {licenseData.licenseStatus}
                      </span>
                    </div>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-950 p-4 rounded-xl border">
                    <span className="text-[10px] font-bold uppercase text-slate-450 block">Expiry / Support Cover</span>
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 mt-1 block">
                      {licenseData.expiresAt ? new Date(licenseData.expiresAt).toLocaleDateString("en-AU") : "Not Applicable / Unactivated"}
                    </span>
                  </div>
                </div>

                {licenseData.licenseStatus !== "active" ? (
                  <form onSubmit={handleActivateLicense} className="space-y-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                    <div className="bg-blue-600/5 dark:bg-blue-600/10 border border-blue-500/20 rounded-xl p-3.5 text-xs text-blue-800 dark:text-blue-300 leading-normal font-sans">
                      <strong>No client details are ever transmitted.</strong> During online activation, only the unique Installation ID, version metadata, and License Key are checked against the CLS authentication registry. If offline, any correctly formatted key is verified locally.
                    </div>
                    
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                        Enter Commercial License Key
                      </label>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          required
                          placeholder="CLSP-XXXX-XXXX-XXXX-XXXX"
                          value={licenseKeyInput}
                          onChange={(e) => setLicenseKeyInput(e.target.value)}
                          className="bg-slate-50/50 dark:bg-slate-950/50 border-slate-200 dark:border-slate-800 text-xs rounded-xl focus:border-blue-500 font-mono"
                        />
                        <Button
                          type="submit"
                          disabled={activatingLicense}
                          className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white font-bold text-xs rounded-xl px-6 cursor-pointer"
                        >
                          {activatingLicense ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Activate"}
                        </Button>
                      </div>
                    </div>
                  </form>
                ) : (
                  <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDeactivateLicense}
                      className="rounded-xl border-slate-200 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/20 font-bold text-xs inline-flex items-center gap-1 cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Deactivate License Key
                    </Button>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* Software Updates Panel */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
              <h2 className="text-base font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2 mb-4">
                <RefreshCw className="w-4 h-4 text-blue-600" />
                Updates Engine
              </h2>
              
              <div className="space-y-4 font-sans text-xs">
                <div className="flex justify-between border-b pb-2 border-slate-100 dark:border-slate-800">
                  <span className="text-slate-450 font-medium">Installed Version:</span>
                  <span className="font-bold text-slate-700 dark:text-slate-350">1.0.0</span>
                </div>
                <div className="flex justify-between border-b pb-2 border-slate-100 dark:border-slate-800">
                  <span className="text-slate-450 font-medium">Release Distribution:</span>
                  <span className="font-bold text-slate-700 dark:text-slate-350">Stable</span>
                </div>

                {updatesData && (
                  <div className="space-y-3 pt-2">
                    <div className="flex justify-between font-bold">
                      <span className="text-slate-450">Latest Version:</span>
                      <span className={updatesData.hasUpdate ? "text-amber-500" : "text-emerald-500"}>
                        {updatesData.latestVersion}
                      </span>
                    </div>
                    {updatesData.hasUpdate ? (
                      <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-amber-800 dark:text-amber-350 leading-relaxed text-[11px]">
                        <strong>Update available!</strong> Size: {formatBytes(updatesData.sizeBytes)}<br/>
                        Changelog: {updatesData.changelog}
                      </div>
                    ) : (
                      <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-xl p-3 text-emerald-800 dark:text-emerald-350 text-[11px] font-medium flex items-center gap-1.5">
                        <Check className="w-4 h-4 text-emerald-500" /> System is fully up to date.
                      </div>
                    )}
                  </div>
                )}

                <div className="pt-2 gap-2 flex flex-col">
                  <Button
                    variant="outline"
                    onClick={handleCheckUpdates}
                    disabled={checkingUpdates}
                    className="w-full rounded-xl border-slate-200 text-slate-650 flex items-center justify-center gap-1.5 text-xs font-bold cursor-pointer h-9"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${checkingUpdates ? "animate-spin" : ""}`} />
                    Check for Updates
                  </Button>
                  {updatesData && updatesData.hasUpdate && (
                    <Button
                      onClick={handleInstallUpdate}
                      disabled={installingUpdate}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-1.5 text-xs font-bold cursor-pointer h-9 shadow shadow-blue-500/10"
                    >
                      {installingUpdate ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                      Install Update Now
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* System Diagnostics / Health View */}
      {activeTab === "diagnostics" && (
        <div className="space-y-6">
          {loadingHealth ? (
            <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-6 shadow-sm flex items-center justify-center py-12 text-slate-400">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading system metrics...
            </div>
          ) : healthData ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 font-sans text-xs">
              {/* Database Health Card */}
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
                <h3 className="text-base font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2 border-b pb-3 border-slate-100 dark:border-slate-800">
                  <Database className="w-4 h-4 text-blue-500" />
                  Database Engine (SQLite)
                </h3>
                <div className="space-y-3 font-mono text-[11px]">
                  <div>
                    <span className="text-slate-400 block mb-0.5">Database File Path</span>
                    <span className="text-slate-650 dark:text-slate-350 break-all select-all font-sans">{healthData.database.path}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Database Size:</span>
                    <span className="text-slate-700 dark:text-slate-350 font-bold">{formatBytes(healthData.database.sizeBytes)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Client Matters count:</span>
                    <span className="text-slate-700 dark:text-slate-350 font-bold">{healthData.database.matterCount} records</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Security Audit rows:</span>
                    <span className="text-slate-700 dark:text-slate-350 font-bold">{healthData.database.logCount} logs</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">User accounts:</span>
                    <span className="text-slate-700 dark:text-slate-350 font-bold">{healthData.database.userCount} users</span>
                  </div>
                  <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-800 dark:text-emerald-350 p-2.5 rounded-xl flex items-center gap-2 mt-2 font-sans font-semibold">
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span>Integrity: PRAGMA integrity_check PASS</span>
                  </div>
                </div>
              </div>

              {/* Host Machine Diagnostics Card */}
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
                <h3 className="text-base font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2 border-b pb-3 border-slate-100 dark:border-slate-800">
                  <Cpu className="w-4 h-4 text-blue-500" />
                  Host Environment
                </h3>
                <div className="space-y-3 font-mono text-[11px]">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Operation System:</span>
                    <span className="text-slate-700 dark:text-slate-350 font-bold uppercase">{healthData.system.platform}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Logical CPU Cores:</span>
                    <span className="text-slate-700 dark:text-slate-350 font-bold">{healthData.system.cpuCount} Cores</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Allocated Process Memory:</span>
                    <span className="text-slate-700 dark:text-slate-350 font-bold">{healthData.system.memoryUsage}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Engine Type:</span>
                    <span className="text-slate-700 dark:text-slate-350 font-bold">NodeJS {healthData.system.nodeVersion || ""}</span>
                  </div>
                  <div className="bg-blue-600/5 border border-blue-500/10 text-blue-800 dark:text-blue-350 p-2.5 rounded-xl flex items-center gap-2 mt-2 font-sans font-semibold">
                    <Server className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    <span>Server Port: 5001 (Listening)</span>
                  </div>
                </div>
              </div>

              {/* Diagnostic Support Utility Card */}
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
                <h3 className="text-base font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2 border-b pb-3 border-slate-100 dark:border-slate-800">
                  <Activity className="w-4 h-4 text-blue-500" />
                  Diagnostics Toolkit
                </h3>
                
                <div className="space-y-3 font-sans">
                  <p className="text-[11px] text-slate-500 leading-normal">
                    Collect log files, dump schemas, or execute self-test routines to send to Complete Law Support agency for technical assistance.
                  </p>
                  
                  <div className="pt-2 space-y-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        const logsStr = JSON.stringify(logs, null, 2);
                        const blob = new Blob([logsStr], { type: "application/json" });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.setAttribute("download", `cls_portal_audit_logs_${Date.now()}.json`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                        toast.success("Audit log file compiled and downloaded!");
                      }}
                      className="w-full text-[10px] uppercase font-bold text-slate-650 h-9 border border-slate-200 dark:border-slate-800 rounded-xl hover:bg-slate-100 justify-start gap-2 cursor-pointer"
                    >
                      <FileText className="w-4 h-4 text-slate-500" /> Export System Logs
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        const diagnosticsStr = JSON.stringify({
                          timestamp: new Date().toISOString(),
                          health: healthData,
                          licensing: licenseData,
                          backups: backups.length
                        }, null, 2);
                        const blob = new Blob([diagnosticsStr], { type: "application/json" });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.setAttribute("download", `cls_portal_diagnostics_${Date.now()}.json`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                        toast.success("Diagnostic dump generated and downloaded!");
                      }}
                      className="w-full text-[10px] uppercase font-bold text-slate-650 h-9 border border-slate-200 dark:border-slate-800 rounded-xl hover:bg-slate-100 justify-start gap-2 cursor-pointer"
                    >
                      <Shield className="w-4 h-4 text-slate-500" /> Export Diagnostic Dump
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
